import {
  formatNative,
  type ClosedPosition,
  type HistoricalMarks,
  type PositionHistoryResponse,
} from "@auto-pools/core";
import { formatMoney, formatPercent, formatPrice, formatToken, shorten } from "../lib/format";
import { stopPolicyText } from "../lib/stopPolicy";
import { priceFrame } from "../lib/priceFrame";
import { useEffect, useState } from "react";
import { api } from "../api/client";
import { useResource } from "../api/useResource";
import {
  ClosedAmounts,
  ClosedPositionCard,
  closedPositionLabel,
  formatClosedWhen,
} from "../components/ClosedPositionCard";
import { OperationStatus } from "../components/OperationStatus";
import { PositionActionModal } from "../components/PositionActionModal";
import { RefreshButton } from "../components/RefreshButton";
import { useRefreshPhase } from "../lib/useRefreshPhase";
import type { Operation } from "../types";

type Tab = "closed" | "operations";
type SelectTab = (tab: Tab, restoreFocus?: boolean) => void;

function HistoryTabs({ tab, onTab }: { tab: Tab; onTab: SelectTab }) {
  const tabs: Tab[] = ["closed", "operations"];
  return (
    <div role="tablist" aria-label="History views" className="history-tabs">
      {tabs.map((value, index) => (
        <button
          key={value}
          id={`history-${value}-tab`}
          role="tab"
          aria-selected={tab === value}
          aria-controls={`history-${value}`}
          tabIndex={tab === value ? 0 : -1}
          onClick={() => {
            onTab(value);
          }}
          onKeyDown={(event) => {
            if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
            event.preventDefault();
            const next = tabs[event.key === "Home" ? 0 : event.key === "End" ? 1 : 1 - index];
            onTab(next, true);
          }}
        >
          {value === "closed" ? "Closed positions" : "Operations"}
        </button>
      ))}
    </div>
  );
}

function HistoryToolbar({
  tab,
  onTab,
  description,
  onRefresh,
  loading,
  failed,
  status,
}: {
  tab: Tab;
  onTab: SelectTab;
  description: string;
  onRefresh: () => void;
  loading: boolean;
  /** The latest read failed: the pill must not confirm an update the page could not load. */
  failed: boolean;
  status?: { role: "status" | "alert"; text: string };
}) {
  const refresh = useRefreshPhase({ pending: loading, failed });
  return (
    <div className="history-toolbar">
      <HistoryTabs tab={tab} onTab={onTab} />
      <p role={status?.role} className={status?.role === "alert" ? "error" : undefined}>
        {status?.text ?? description}
      </p>
      <RefreshButton
        phase={refresh.phase}
        ariaLabel="Refresh"
        title="Refresh history"
        onClick={() => {
          if (refresh.start()) onRefresh();
        }}
      />
    </div>
  );
}

export function HistoryPage({
  refresh,
  tab,
  onTab,
}: {
  refresh: number;
  tab: Tab;
  onTab: (tab: Tab) => void;
}) {
  const [operationId, setOperationId] = useState<string>();
  const [focusTarget, setFocusTarget] = useState<Tab | null>(null);
  const selectTab: SelectTab = (value, restoreFocus = false) => {
    if (restoreFocus) setFocusTarget(value);
    setOperationId(undefined);
    onTab(value);
  };
  useEffect(() => {
    if (!focusTarget) return;
    if (focusTarget === tab) document.getElementById(`history-${tab}-tab`)?.focus();
    setFocusTarget(null);
  }, [focusTarget, tab]);
  return (
    <>
      <section role="tabpanel" id={`history-${tab}`} aria-labelledby={`history-${tab}-tab`}>
        {tab === "closed" ? (
          <ClosedPositions
            onTab={selectTab}
            onOperation={(id) => {
              setOperationId(id);
              onTab("operations");
            }}
          />
        ) : (
          <Operations refresh={refresh} operationId={operationId} onTab={selectTab} />
        )}
      </section>
    </>
  );
}
function Operations({
  refresh,
  operationId,
  onTab,
}: {
  refresh: number;
  operationId?: string;
  onTab: SelectTab;
}) {
  const operations = useResource<{ items: Operation[] }>(
    `operations:${operationId ?? "all"}:${String(refresh)}`,
    async (signal) =>
      operationId
        ? {
            items: [
              await api<Operation>(`/operations/${encodeURIComponent(operationId)}`, {
                signal,
                cache: "no-store",
              }),
            ],
          }
        : api("/operations", { signal, cache: "no-store" }),
    15_000,
  );
  const [resume, setResume] = useState<Operation | null>(null);
  const grouped = groupOperations(operations.data?.items ?? []);
  return (
    <>
      <HistoryToolbar
        tab="operations"
        onTab={onTab}
        description="Durable steps, retries, signatures and errors"
        onRefresh={operations.reload}
        loading={operations.loading}
        failed={operations.error !== null}
      />
      {operations.data && (operations.loading || operations.error) && (
        <p role="status">
          Showing previous journal data; {operations.loading ? "refreshing…" : "stale"}
        </p>
      )}
      {operations.error && <p className="inline-alert error">{operations.error.message}</p>}
      <section className="operations-list" aria-busy={operations.loading}>
        {operations.loading && !operations.data && (
          <div className="operation-skeleton" role="status">
            Reading journal…
          </div>
        )}
        {grouped.map(({ label, className, items }) =>
          items.length ? (
            <section className={`operation-group ${className}`} key={className} aria-label={label}>
              <h2>{label}</h2>
              <div>
                {items.map((operation) => (
                  <OperationStatus key={operation.id} operation={operation} onResume={setResume} />
                ))}
              </div>
            </section>
          ) : null,
        )}
        {operations.data?.items.length === 0 && (
          <div className="empty-state">
            <b>The journal is empty</b>
            <span>Only operations accepted by the server appear here.</span>
          </div>
        )}
      </section>
      {resume && (
        <PositionActionModal
          title="Resume operation?"
          description={`The server first reconciles the on-chain outcome of operation ${resume.id}. A send or rebuild remains constrained by its existing authorization.`}
          endpoint={`/operations/${encodeURIComponent(resume.id)}/resume`}
          submitLabel="Reconcile and resume"
          onAccepted={() => {
            setResume(null);
            operations.reload();
          }}
          onClose={() => {
            setResume(null);
          }}
        />
      )}
    </>
  );
}

function groupOperations(items: Operation[]) {
  const attentionStates = new Set(["blocked", "failed", "unknown", "needs-attention"]);
  const finishedStates = new Set(["completed", "cancelled"]);
  return [
    {
      label: "Attention",
      className: "attention",
      items: items.filter((item) => attentionStates.has(item.state)),
    },
    {
      label: "In progress",
      className: "in-progress",
      items: items.filter(
        (item) => !attentionStates.has(item.state) && !finishedStates.has(item.state),
      ),
    },
    {
      label: "Finished",
      className: "finished",
      items: items.filter((item) => finishedStates.has(item.state)),
    },
  ];
}

function ClosedPositions({
  onOperation,
  onTab,
}: {
  onOperation: (id: string) => void;
  onTab: SelectTab;
}) {
  const [cursors, setCursors] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const cursor = cursors.at(-1);
  return (
    <ClosedPositionPage
      key={cursor ?? "first"}
      expanded={expanded}
      setExpanded={setExpanded}
      cursor={cursor}
      pageNumber={cursors.length + 1}
      onPrevious={() => {
        setCursors((old) => old.slice(0, -1));
      }}
      onNext={(next) => {
        setCursors((old) => [...old, next]);
      }}
      onOperation={onOperation}
      onTab={onTab}
    />
  );
}

function ClosedPositionPage({
  expanded,
  setExpanded,
  cursor,
  pageNumber,
  onPrevious,
  onNext,
  onOperation,
  onTab,
}: {
  expanded: Set<string>;
  setExpanded: React.Dispatch<React.SetStateAction<Set<string>>>;
  cursor: string | undefined;
  pageNumber: number;
  onPrevious: () => void;
  onNext: (cursor: string) => void;
  onOperation: (id: string) => void;
  onTab: SelectTab;
}) {
  const history = useResource<PositionHistoryResponse>(`closed:${cursor ?? "first"}`, (signal) =>
    api(`/positions/history${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`, {
      signal,
      cache: "no-store",
    }),
  );
  const historyStatus = history.error
    ? {
        role: "alert" as const,
        text: `${history.data ? "Showing previous page data; " : ""}${history.error.message}`,
      }
    : history.loading
      ? {
          role: "status" as const,
          text: history.data
            ? "Showing previous page data; refreshing…"
            : "Loading closed positions…",
        }
      : undefined;
  return (
    <div className="closed-history">
      <HistoryToolbar
        tab="closed"
        onTab={onTab}
        description="Finalized results · audit in Details"
        onRefresh={history.reload}
        loading={history.loading}
        failed={history.error !== null}
        status={historyStatus}
      />
      {!history.loading && history.data?.items.length === 0 && (
        <div className="empty-state closed-history-empty">No verified closed positions yet.</div>
      )}
      <div
        className="closed-position-list"
        aria-label="Closed positions"
        aria-busy={history.loading}
      >
        {history.loading && !history.data && (
          <div
            className="closed-history-skeleton"
            role="status"
            aria-label="Loading closed positions"
          />
        )}
        {history.data?.items.map((item) => {
          const isExpanded = expanded.has(item.positionId);
          return (
            <ClosedPositionCard
              key={item.positionId}
              item={item}
              expanded={isExpanded}
              onToggleDetails={() => {
                setExpanded((old) => {
                  const next = new Set(old);
                  if (!next.delete(item.positionId)) next.add(item.positionId);
                  return next;
                });
              }}
              details={<Details item={item} onOperation={onOperation} />}
            />
          );
        })}
      </div>
      {(cursor ?? history.data?.nextCursor) && (
        <footer className="closed-pagination" aria-label="Closed position pages">
          <button
            className="button ghost"
            disabled={!cursor || history.loading}
            onClick={onPrevious}
          >
            Previous page
          </button>
          <span>Page {pageNumber}</span>
          <button
            className="button ghost"
            disabled={!history.data?.nextCursor || history.loading || !!history.error}
            onClick={() => {
              const next = history.data?.nextCursor;
              if (next) onNext(next);
            }}
          >
            Next page
          </button>
        </footer>
      )}
    </div>
  );
}
const Amounts = ClosedAmounts;
export { Amounts };
const tokenSymbol = (item: ClosedPosition, mint: string) =>
  [item.tokenA, item.tokenB].find((token) => token.mint === mint)?.symbol ??
  (mint === "So11111111111111111111111111111111111111112" ? "SOL" : shorten(mint, 6));
const historyTime = (value: number) => formatClosedWhen(new Date(value).toISOString());
export function Marks({ value, item }: { value: HistoricalMarks | null; item?: ClosedPosition }) {
  if (!value) return <>Unavailable</>;
  const confirmedAt =
    value.poolCommitment === "confirmed" && value.observedAt !== null
      ? historyTime(value.observedAt)
      : null;
  return (
    <>
      {value.provenance
        ? "Recorded price sources. "
        : confirmedAt
          ? `Confirmed pool read observed ${confirmedAt}. USDC assumed $1. `
          : "Pool observation time unknown. "}
      {!value.provenance && confirmedAt && value.anchorObservedAt !== null
        ? ` SOL anchor observed ${historyTime(value.anchorObservedAt)}.`
        : ""}
      {Object.entries(value.prices).map(([mint, price]) => {
        const provenance = value.provenance?.[mint];
        const detail = !provenance
          ? "Price provenance unavailable."
          : provenance.quality === "historical-estimate"
            ? `Historical estimated price; sample ${provenance.sampleStart === null ? "unknown" : historyTime(provenance.sampleStart)}–${provenance.sampleEnd === null ? "unknown" : historyTime(provenance.sampleEnd)}; target boundary ${historyTime(provenance.targetAt)}; obtained ${provenance.obtainedAt === null ? "unknown" : historyTime(provenance.obtainedAt)}.`
            : provenance.quality === "legacy-unknown"
              ? `Legacy price; observation time unknown; target boundary ${historyTime(provenance.targetAt)}.`
              : provenance.sampleEnd !== null
                ? `${provenance.provider === "opening-quote" ? "Opening quote" : "Captured price"}; observed ${historyTime(provenance.sampleEnd)}; target boundary ${historyTime(provenance.targetAt)}.`
                : `Price observation time unknown; target boundary ${historyTime(provenance.targetAt)}.`;
        return (
          <small key={mint} title={`${mint}: exact price $${price}`}>
            {item ? tokenSymbol(item, mint) : shorten(mint, 6)}: $
            {formatPrice(price, "long", Object.values(value.prices))}. {detail}
          </small>
        );
      })}
    </>
  );
}
const detailedPrice = (value: string | undefined) =>
  value === undefined ? "disabled" : formatPrice(value, "long");
const unavailableReasons: Record<string, string> = {
  COMPLETION_MARKS_UNAVAILABLE: "Historical closing USD prices were not recorded",
  TOPUP_MARKS_UNAVAILABLE: "Historical close top-up USD prices are unknown",
  RETAINED_RENT_UNAVAILABLE: "Retained refundable rent is unknown",
  CAPITAL_MARKS_UNAVAILABLE: "Historical capital USD prices are unknown",
};
const unavailableReason = (code: string) =>
  unavailableReasons[code] ?? "Some historical evidence is unknown";
export function SolAmount({ lamports }: { lamports: string | null }) {
  if (lamports === null) return <>Unavailable</>;
  try {
    const display = formatToken(formatNative(BigInt(lamports), 9));
    return <span title={`${lamports} lamports`}>{display} SOL</span>;
  } catch {
    return <>Unavailable</>;
  }
}
function Details({
  item: p,
  onOperation,
}: {
  item: ClosedPosition;
  onOperation: (id: string) => void;
}) {
  const setup = p.openingSetup,
    exit = p.exitSettings,
    trigger = p.closeReason.trigger,
    // Out-of-range closes report the range edge the price stayed beyond, not a stop threshold —
    // `trigger.threshold` is the same field, but the wording must not claim a stop fired.
    outOfRange = p.closeReason.origin === "automatic" && p.closeReason.rule === "out-of-range",
    // The card's rule frame: every price and side below is printed in it, never in the raw
    // canonical or setup frame the server actually stored the value in.
    frame = priceFrame(p.tokenA, p.tokenB),
    shownSetup = setup ? frame.fromSetup(setup) : null,
    displayExit = exit ? frame.policy(exit) : null;
  return (
    <div className="history-details">
      <p
        title={
          trigger
            ? outOfRange
              ? `Exact observed price ${trigger.observedPrice}; edge ${trigger.threshold}; canonical · ${frame.canonicalUnit} (exact)`
              : `Exact observed price ${trigger.observedPrice}; threshold ${trigger.threshold}; canonical · ${frame.canonicalUnit} (exact)`
            : undefined
        }
      >
        <strong>Close reason: {closedPositionLabel(p, frame)}</strong>
        {trigger &&
          (outOfRange
            ? `; ${frame.side(trigger.side)} edge ${formatPrice(frame.price(trigger.threshold), "long")} (${frame.unit})`
            : `; observed ${formatPrice(frame.price(trigger.observedPrice), "long", [frame.price(trigger.threshold)])} at threshold ${formatPrice(frame.price(trigger.threshold), "long", [frame.price(trigger.observedPrice)])} (${frame.unit})`)}
      </p>
      <p>Verified at reconciliation; these are observation times, not exact block times.</p>
      <dl>
        <dt>Opening settings</dt>
        <dd
          title={
            setup
              ? `Exact prices ${setup.lowerPrice}–${setup.upperPrice}; stops ${setup.initialExitPolicy.lowerTrigger ?? "disabled"}, ${setup.initialExitPolicy.upperTrigger ?? "disabled"}; as entered at open · ${frame.unitOf(setup.baseMint, setup.quoteMint)} (exact)`
              : undefined
          }
        >
          {setup && shownSetup
            ? `${formatPrice(shownSetup.lowerPrice, "long", [shownSetup.upperPrice])}–${formatPrice(shownSetup.upperPrice, "long", [shownSetup.lowerPrice])}; auto exit ${shownSetup.initialExitPolicy.enabled ? stopPolicyText(shownSetup.initialExitPolicy) : "off"}; lower stop ${detailedPrice(shownSetup.initialExitPolicy.lowerTrigger)}, upper stop ${detailedPrice(shownSetup.initialExitPolicy.upperTrigger)}; return token ${tokenSymbol(p, shownSetup.initialExitPolicy.targetMint)}; ${frame.unit}`
            : "Unavailable"}
        </dd>
        <dt>Exit settings</dt>
        <dd
          title={
            exit
              ? `Exact settings ${JSON.stringify(exit)}; canonical · ${frame.canonicalUnit} (exact)`
              : undefined
          }
        >
          {exit && displayExit
            ? `Return token ${tokenSymbol(p, displayExit.targetMint)}; auto exit ${displayExit.enabled ? stopPolicyText(displayExit) : "off"}; lower stop ${detailedPrice(displayExit.lowerTrigger)}, upper stop ${detailedPrice(displayExit.upperTrigger)} (${frame.unit})`
            : "Unavailable"}
        </dd>
        <dt>Legacy asset audit · opening funding</dt>
        <dd>
          <Amounts values={p.openingFunding ? [p.openingFunding] : null} item={p} />
        </dd>
        <dt>Legacy asset audit · opening expense reserve</dt>
        <dd>
          <Amounts values={p.openingExpenseReserve ? [p.openingExpenseReserve] : null} item={p} />
        </dd>
        <dt>Legacy asset audit · additional close top-ups</dt>
        <dd>
          <Amounts values={p.closeTopups} item={p} />
        </dd>
        <dt>Legacy asset audit · admitted capital</dt>
        <dd>{formatMoney(p.capitalUsd)}; includes funding, expense reserves and close top-ups</dd>
        <dt>Legacy asset audit · cash settlement</dt>
        <dd>
          <Amounts values={p.cashReturned} item={p} />
          Includes unused reserves and refunded rent; these are not added again.
        </dd>
        <dt>Retained refundable account deposits</dt>
        <dd>
          <Amounts values={p.retainedRefundableRent ? [p.retainedRefundableRent] : null} item={p} />
        </dd>
        <dt>Legacy asset audit · total returned assets</dt>
        <dd>{formatMoney(p.assetsReturnedUsd)} (cash plus retained refundable deposits)</dd>
        <dt>Legacy asset result</dt>
        <dd>
          {formatMoney(p.netResultUsd)} · {formatPercent(p.periodReturnPercent)} for period
        </dd>
        <dt>Legacy asset annualized return</dt>
        <dd>
          {formatPercent(p.simpleAnnualizedReturnPercent)}; retained for the original asset audit
          and not used by the cash result above.
        </dd>
        <dt>Fee APR / APY</dt>
        <dd>Unavailable; fee and reward yield are not separately proven.</dd>
        <dt>Actual network fees (including failed attempts)</dt>
        <dd>
          <SolAmount lamports={p.costs.networkFeeLamports} />
        </dd>
        <dt>Net rent paid / nonrefundable rent</dt>
        <dd>
          <SolAmount lamports={p.costs.netRentPaidLamports} /> /{" "}
          <SolAmount lamports={p.costs.nonrefundableRentLamports} />
        </dd>
        <dt>Swap value difference at frozen opening marks</dt>
        <dd>
          {formatMoney(p.costs.swapValueDifferenceUsdAtOpeningMarks)}
          {p.costs.actualSwaps?.map((s, i) => (
            <div key={i}>
              <Amounts values={[s.input]} item={p} /> → <Amounts values={[s.output]} item={p} />
            </div>
          ))}
        </dd>
        <dt>Valuation basis</dt>
        <dd>
          These memos are already included in the legacy asset result and must not be subtracted
          again. Historical marks are indicative, not execution prices.
        </dd>
        <dt>Opening marks</dt>
        <dd>
          {p.valuation.legacyOpeningMarks && !p.valuation.opening ? (
            "Frozen legacy quote prices; observation time unknown"
          ) : (
            <Marks value={p.valuation.opening} item={p} />
          )}
        </dd>
        <dt>Close admission marks</dt>
        <dd>
          <Marks value={p.valuation.closeAdmission} item={p} />
        </dd>
        <dt>Completion marks</dt>
        <dd>
          <Marks value={p.valuation.completion} item={p} />
        </dd>
        <dt>Financial report metadata</dt>
        <dd>
          {p.reportStatus
            ? `${p.reportStatus.status}; ${String(p.reportStatus.attempts)} attempt${p.reportStatus.attempts === 1 ? "" : "s"}${p.reportStatus.reasonCode && /^[A-Z][A-Z0-9_]{0,63}$/.test(p.reportStatus.reasonCode) ? `; diagnostic code ${p.reportStatus.reasonCode}` : ""}`
            : "Unavailable"}
        </dd>
      </dl>
      {p.valuation.unavailable.length > 0 && (
        <p>Unavailable evidence: {p.valuation.unavailable.map(unavailableReason).join("; ")}</p>
      )}
      <div className="history-actions">
        {[
          ["Opening operation", p.openingOperationId],
          ["Closing operation", p.closingOperationId],
        ].map(([label, id]) => (
          <a
            key={id}
            href={`#operation-${id}`}
            onClick={(event) => {
              event.preventDefault();
              onOperation(id);
            }}
          >
            {label}: {id}
          </a>
        ))}
      </div>
    </div>
  );
}
